import type { ResumeDto, UpdateResumeDto } from "@stylingcv/dto";
import { defaultResumeData } from "@stylingcv/schema";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { trackResumeEvent } from "@/client/utils/gtm";

// Same sanitization function we use for guest resumes
const sanitizeResumeData = (data: any) => {
  const sanitizedData = structuredClone(defaultResumeData);

  if (data?.data) {
    // Merge top-level properties of the resume data
    Object.assign(sanitizedData, data.data);

    // Merge nested objects with proper fallbacks
    if (data.data.basics) {
      Object.assign(sanitizedData.basics, data.data.basics);
    }

    if (data.data.sections) {
      Object.assign(sanitizedData.sections, data.data.sections);
    }

    if (data.data.metadata) {
      // CRITICAL: Preserve the AI-generated locale before merging with defaults
      const originalLocale = data.data.metadata.locale;

      Object.assign(sanitizedData.metadata, data.data.metadata);

      // Ensure the locale is preserved after merge
      if (originalLocale && originalLocale !== "en-US") {
        sanitizedData.metadata.locale = originalLocale;
      }


      // Normalize theme.isFree to expected string values if present
      try {
        const raw = (sanitizedData as any)?.metadata?.theme?.isFree;
        if (raw !== undefined) {
          (sanitizedData as any).metadata.theme.isFree =
            typeof raw === "boolean" ? (raw ? "YES" : "NO") : String(raw);
        }
      } catch {
        // ignore
      }
    }

    // Update the data property with sanitized data
    return {
      ...data,
      data: sanitizedData,
    };
  }

  return data;
};

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

export const updateResume = async (data: UpdateResumeDto, retryCount = 0): Promise<ResumeDto | undefined> => {
  const setIsSaving = useBuilderStore.getState().setIsSaving;
  setIsSaving(true);

  try {
    const sanitizedData = sanitizeResumeData(data);

    const response = await axios.patch<ResumeDto, AxiosResponse<ResumeDto>, UpdateResumeDto>(
      `/resume/${data.id}`,
      sanitizedData,
    );

    trackResumeEvent.updated();

    queryClient.setQueryData<ResumeDto>(["resume", { id: response.data.id }], response.data);

    queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
      if (!cache) return [response.data];
      return cache.map((resume) => {
        if (resume.id === response.data.id) return response.data;
        return resume;
      });
    });

    return response.data;
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1)));
      return updateResume(data, retryCount + 1);
    }

    // All retries exhausted -- rollback local state
    try {
      const temporalStore = useResumeStore.temporal.getState();
      temporalStore.undo();
    } catch {
      // Temporal undo not available (e.g., first change), skip rollback
    }

    toast({
      variant: "error",
      title: "Save failed",
      description: "Your changes could not be saved. They have been reverted. Please try again.",
    });

    return undefined;
  } finally {
    setIsSaving(false);
  }
};

export const debouncedUpdateResume = debounce(updateResume, 500);

export const useUpdateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateResumeFn,
  } = useMutation({
    mutationFn: updateResume,
  });

  return { updateResume: updateResumeFn, loading, error };
};
